import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import questionnaireScopeApi from "../../api/questionnaireScopeApi";
import questionnaireApplicationsApi from "../../api/questionnaireApplicationsApi";
import nom35SecIAnalyticsApi from "../../api/nom35SecIAnalyticsApi";
import ageRanges from "../../data/ageRanges";
import biologicalSexList from "../../data/biologicalSex";
import Nom035SummaryChart from "../../components/analytics/Nom035SummaryChart";
import { generateNom035SecIReport } from "../../utils/Nom035SecIReportGenerator";
import calculateNom035MinimumRespondents from "../../utils/nom035SampleSize";
import styles from "./Nom035AnalyticsPage.module.css";

export default function Nom035SecIAnalyticsPage() {
  const { companyId: paramCompanyId, branchId, applicationId } = useParams();
  const { state } = useLocation();

  const companyId = paramCompanyId || state?.companyId;
  const analyticsScopeId = branchId || companyId;
  const areaScopeId = analyticsScopeId;
  const [companyJobAreas, setCompanyJobAreas] = useState([]);
  const [usesEmployeeCredentials, setUsesEmployeeCredentials] = useState(false);
  const [companyData, setCompanyData] = useState({ name: "", employeesNumber: 0 });
  const [totalResponses, setTotalResponses] = useState(null);
  const [generating, setGenerating] = useState(false);

  const [respondents, setRespondents] = useState([]);
  const [empSortBy, setEmpSortBy] = useState("email");
  const [empSortOrder, setEmpSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const pageSize = 15;

  useEffect(() => {
    if (!analyticsScopeId || !areaScopeId) return;
    let cancelled = false;
    setCurrentPage(1);
    setRespondents([]);
    setTotalResponses(null);
    setCompanyJobAreas([]);
    (async () => {
      try {
        const comp = await questionnaireScopeApi.getById(areaScopeId);
        if (cancelled) return;
        setCompanyData({
          name: comp.name,
          employeesNumber: 0,
        });
      } catch (err) {
        console.error("Error cargando empresa/sucursal:", err);
      }

      try {
        if (applicationId) {
          const config = await questionnaireApplicationsApi.getNom035Config(applicationId);
          if (cancelled) return;
          setCompanyJobAreas(config.jobAreas || []);
          setUsesEmployeeCredentials(Boolean(config.usesEmployeeCredentials));
          setEmpSortBy(config.usesEmployeeCredentials ? "employee_number" : "email");
        } else {
          const config = await nom35SecIAnalyticsApi.getScopeConfig(analyticsScopeId);
          if (cancelled) return;
          setCompanyJobAreas(config.jobAreas || []);
          setUsesEmployeeCredentials(false);
          setEmpSortBy("email");
        }
      } catch (err) {
        if (cancelled) return;
        setCompanyJobAreas([]);
        setUsesEmployeeCredentials(false);
        console.error("Error cargando areas de la aplicacion NOM-035 Guia I:", err);
      }

      try {
        const { totalResponses } =
          await nom35SecIAnalyticsApi.getResponsesCountByCompanyId(
            analyticsScopeId,
            applicationId ? { applicationId } : {}
          );
        if (cancelled) return;
        setTotalResponses(totalResponses);
      } catch (err) {
        console.error("Error obteniendo total de encuestados Nom035 Guia I:", err);
      }

      try {
        const { respondants, totalEmployees } =
          await nom35SecIAnalyticsApi.getRespondantsByCompanyId(areaScopeId, applicationId);
        if (cancelled) return;
        setRespondents(respondants);
        setCompanyData((current) => ({ ...current, employeesNumber: totalEmployees }));
      } catch (err) {
        console.error("Error obteniendo listado de empleados Nom035 Guia I:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [analyticsScopeId, areaScopeId, applicationId]);

  const jobAreaOptions = companyJobAreas;
  const filterConfigs = [
    { name: "ageRange", label: "Rango de edad", options: ageRanges },
    { name: "jobArea", label: "Área de trabajo", options: jobAreaOptions },
    { name: "biologicalSex", label: "Sexo biológico", options: biologicalSexList },
  ];

  const N = companyData.employeesNumber;
  const minimumRespondents = calculateNom035MinimumRespondents(N);

  const sortedRespondents = useMemo(() => {
    return [...respondents].sort((a, b) => {
      const field = empSortBy;
      if (a[field] < b[field]) return empSortOrder === "asc" ? -1 : 1;
      if (a[field] > b[field]) return empSortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [respondents, empSortBy, empSortOrder]);

  const totalPages = Math.ceil(sortedRespondents.length / pageSize) || 1;
  const displayedRespondents = sortedRespondents.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const onSort = (column) => {
    if (empSortBy === column) {
      setEmpSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setEmpSortBy(column);
    setEmpSortOrder("asc");
  };

  const handleDownloadRespondentsExcel = async () => {
    if (!analyticsScopeId) return;
    try {
      setDownloading(true);
      await nom35SecIAnalyticsApi.downloadRespondantsByCompanyIdExcel(areaScopeId, applicationId);
    } catch (err) {
      console.error("Error descargando Excel Nom035 Guia I:", err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPdfReport = async () => {
    if (!analyticsScopeId) return;
    try {
      setGenerating(true);
      await generateNom035SecIReport(
        analyticsScopeId,
        applicationId ? { applicationId } : {},
        areaScopeId
      );
    } catch (err) {
      console.error("Error generando reporte PDF Nom035 Guia I:", err);
    } finally {
      setGenerating(false);
    }
  };

  const SortArrow = ({ column }) => {
    const isActive = empSortBy === column;
    if (!isActive) {
      return (
        <span className={styles.sortArrow}>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        </span>
      );
    }

    return (
      <span className={styles.sortArrow}>
        {empSortOrder === "asc" ? (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="4,0 8,8 0,8" fill="currentColor" />
          </svg>
        ) : (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <polygon points="0,0 8,0 4,8" fill="currentColor" />
          </svg>
        )}
      </span>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <span className={styles.eyebrow}>ANALÍTICA DE BIENESTAR</span>
          <h2 className={styles.title}>NOM-035 Guía I</h2>
          <p className={styles.subheader}>
            {applicationId ? "Resultados de " : "Resultados de todas las aplicaciones de "}<span className={styles.companyName}>{companyData.name || "la empresa"}</span>
          </p>
        </div>
        <button className={styles.downloadButton} onClick={handleDownloadPdfReport} disabled={generating}>
          {generating ? "Generando reporte…" : "Descargar reporte PDF"}
        </button>
      </header>

      {totalResponses !== null && (
        <div className={styles.summaryCards}>
          <div className={`${styles.summaryCard} ${styles.summaryCardAccent}`}>
            <span>{applicationId ? "Encuestados" : "Respuestas acumuladas"}</span>
            <strong>{totalResponses}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>{applicationId ? "Total de empleados" : "Registros en aplicaciones"}</span>
            <strong>{companyData.employeesNumber}</strong>
          </div>
          {applicationId && <div className={styles.summaryCard}>
            <span>Mínimo requerido</span>
            <strong>{minimumRespondents}</strong>
          </div>}
        </div>
      )}

      {!applicationId && <p className={styles.subheader}>El consolidado suma las participaciones de todas las aplicaciones. Una persona puede aparecer más de una vez.</p>}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Nom035 Guia I - Acontecimientos Traumáticos Severos
        </h3>
        <div className={styles.fullWidth}>
          <Nom035SummaryChart
            companyId={analyticsScopeId}
            clinical
            title="Resumen clínico (Guía I)"
            filterConfigs={filterConfigs}
            analyticsApi={nom35SecIAnalyticsApi}
            applicationId={applicationId}
            chartIdPrefix="nom035-seci"
          />
        </div>
      </section>

      <hr className={styles.separator} />

      <section className={`${styles.section} ${styles.responsesSection}`}>
        <div className={styles.tableHeader}>
          <div>
            <span className={styles.eyebrow}>DETALLE</span>
            <h3 className={styles.sectionTitle}>Listado de empleados</h3>
            <p>{respondents.length} {applicationId ? "empleados registrados" : "registros en aplicaciones"}</p>
          </div>
          <button
            onClick={handleDownloadRespondentsExcel}
            disabled={downloading || !analyticsScopeId}
            className={styles.exportButton}
            title="Exportar listado de empleados (NOM-035 Guía I) a Excel"
          >
            {downloading ? "Descargando…" : "Exportar Excel"}
          </button>
        </div>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                {usesEmployeeCredentials ? (
                  <>
                    <th className={styles.clickable} onClick={() => onSort("employee_number")}>
                      Número de empleado <SortArrow column="employee_number" />
                    </th>
                    <th className={styles.clickable} onClick={() => onSort("employee_code")}>
                      Código de empleado <SortArrow column="employee_code" />
                    </th>
                  </>
                ) : (
                  <th className={styles.clickable} onClick={() => onSort("email")}>
                    {applicationId ? "Correo" : "Correo / número de empleado"} <SortArrow column="email" />
                  </th>
                )}
                <th className={styles.clickable} onClick={() => onSort("status")}>
                  Estado <SortArrow column="status" />
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedRespondents.map((r, i) => (
                <tr key={`${r.email || `${r.employee_number}-${r.employee_code}`}-${i}`}>
                  <td>{(currentPage - 1) * pageSize + i + 1}</td>
                  {usesEmployeeCredentials ? (
                    <>
                      <td>{r.employee_number}</td>
                      <td>{r.employee_code}</td>
                    </>
                  ) : <td>{r.email || `${r.employee_number || "—"} / ${r.employee_code || "—"}`}</td>}
                  <td>{r.status}</td>
                </tr>
              ))}
              {displayedRespondents.length === 0 && (
                <tr>
                  <td colSpan={usesEmployeeCredentials ? 4 : 3} className={styles.message}>
                    No hay empleados para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className={styles.paginationButton}
          >
            Anterior
          </button>
          <span className={styles.pageInfo}>
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={styles.paginationButton}
          >
            Siguiente
          </button>
        </div>
      </section>
    </div>
  );
}
